import { useEffect, useState } from 'react';
import { Download, Timer, Gauge, CheckCircle2, Hourglass } from 'lucide-react';
import api from '@/lib/api';
import { exportCsv } from '@/lib/csv';
import { useSort, SortTh, TableHeadRow } from '@/components/ui/sortable-table';

interface RepRow {
  userId: string;
  name: string;
  assigned: number;
  responded: number;
  medianSeconds: number;
  avgSeconds: number;
  withinSlaRate: number;
  responseRate: number;
}

interface ResponseTime {
  slaSeconds: number;
  totalAssigned: number;
  responded: number;
  pending: number;
  medianSeconds: number;
  avgSeconds: number;
  withinSlaRate: number;
  responseRate: number;
  leaderboard: RepRow[];
}

interface Props {
  dateFrom: string;
  dateTo: string;
}

/** Human-friendly duration: 45s, 2m 05s, 1h 03m. */
function fmtSecs(secs: number): string {
  if (!secs || secs < 0) return '—';
  if (secs < 60) return `${secs}s`;
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  if (m < 60) return `${m}m ${String(s).padStart(2, '0')}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${String(m % 60).padStart(2, '0')}m`;
}

export function ResponseTimeReport({ dateFrom, dateTo }: Props) {
  const [data, setData] = useState<ResponseTime | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api
      .get('/reports/response-time', { params: { dateFrom, dateTo } })
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [dateFrom, dateTo]);

  const { sorted, sort, onSort } = useSort(data?.leaderboard ?? []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-[#6366F1] border-t-transparent" />
      </div>
    );
  }

  if (!data || data.totalAssigned === 0) {
    return <p className="text-sm text-slate-400 py-12 text-center">No assigned-lead response data for this range</p>;
  }

  const cards = [
    {
      label: 'Median Response',
      value: fmtSecs(data.medianSeconds),
      subtitle: `Avg ${fmtSecs(data.avgSeconds)}`,
      icon: <Timer className="h-5 w-5 text-[#6366F1]" />,
      iconBg: 'bg-indigo-100',
    },
    {
      label: 'Within SLA',
      value: `${data.withinSlaRate}%`,
      subtitle: `Target ${fmtSecs(data.slaSeconds)}`,
      icon: <CheckCircle2 className="h-5 w-5 text-emerald-500" />,
      iconBg: 'bg-emerald-100',
    },
    {
      label: 'Response Rate',
      value: `${data.responseRate}%`,
      subtitle: `${data.responded} of ${data.totalAssigned} assigned`,
      icon: <Gauge className="h-5 w-5 text-amber-500" />,
      iconBg: 'bg-amber-100',
    },
    {
      label: 'Awaiting Response',
      value: data.pending,
      subtitle: 'No first reply yet',
      icon: <Hourglass className="h-5 w-5 text-rose-500" />,
      iconBg: 'bg-rose-100',
    },
  ];

  const handleExport = () => {
    exportCsv('response_time', data.leaderboard, [
      { key: 'name', label: 'Rep' },
      { key: 'assigned', label: 'Assigned' },
      { key: 'responded', label: 'Responded' },
      { key: 'medianSeconds', label: 'Median (s)' },
      { key: 'avgSeconds', label: 'Avg (s)' },
      { key: 'withinSlaRate', label: 'Within SLA %' },
      { key: 'responseRate', label: 'Response %' },
    ]);
  };

  return (
    <div className="space-y-5">
      {/* KPI Cards */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-4">
        {cards.map(card => (
          <div key={card.label} className="bg-white rounded-xl p-5 shadow-sm">
            <div className={`h-10 w-10 rounded-lg flex items-center justify-center mb-3 ${card.iconBg}`}>
              {card.icon}
            </div>
            <div className="text-2xl font-bold text-[#1E293B]">{card.value}</div>
            <div className="text-sm font-medium text-[#64748B] mt-0.5">{card.label}</div>
            <div className="text-xs text-[#94A3B8] mt-0.5">{card.subtitle}</div>
          </div>
        ))}
      </div>

      <div className="flex justify-end">
        <button
          onClick={handleExport}
          className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-semibold rounded-lg border border-slate-200 bg-white text-slate-700 hover:border-[#6366F1] hover:text-[#6366F1] transition-colors"
        >
          <Download className="h-3.5 w-3.5" />
          Export CSV
        </button>
      </div>

      {/* Leaderboard */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <TableHeadRow>
                <SortTh label="Rep" sortKey="name" sort={sort} onSort={onSort} />
                <SortTh label="Assigned" sortKey="assigned" sort={sort} onSort={onSort} align="right" />
                <SortTh label="Responded" sortKey="responded" sort={sort} onSort={onSort} align="right" />
                <SortTh label="Median" sortKey="medianSeconds" sort={sort} onSort={onSort} align="right" />
                <SortTh label="Avg" sortKey="avgSeconds" sort={sort} onSort={onSort} align="right" />
                <SortTh label="Within SLA" sortKey="withinSlaRate" sort={sort} onSort={onSort} align="right" />
                <SortTh label="Response %" sortKey="responseRate" sort={sort} onSort={onSort} align="right" />
              </TableHeadRow>
            </thead>
            <tbody>
              {sorted.map(m => (
                <tr key={m.userId} className="border-b border-gray-50 hover:bg-gray-50/50">
                  <td className="px-4 py-3 font-medium text-slate-800">{m.name}</td>
                  <td className="px-4 py-3 text-right text-slate-700">{m.assigned}</td>
                  <td className="px-4 py-3 text-right text-slate-700">{m.responded}</td>
                  <td className="px-4 py-3 text-right font-medium text-slate-800">{fmtSecs(m.medianSeconds)}</td>
                  <td className="px-4 py-3 text-right text-slate-600">{fmtSecs(m.avgSeconds)}</td>
                  <td className="px-4 py-3 text-right">
                    <span className={`font-medium ${m.withinSlaRate >= 80 ? 'text-emerald-600' : m.withinSlaRate >= 50 ? 'text-amber-600' : 'text-rose-500'}`}>
                      {m.withinSlaRate}%
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right text-[#6366F1] font-medium">{m.responseRate}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
